import React from "react";
import { AbsoluteFill, interpolate, spring, useCurrentFrame, useVideoConfig } from "remotion";
import { Bg, DISPLAY, INK, LIME, PINK, popIn, SNAPPY, UI, WHITE } from "./theme";

/* ════════════════════════════════════════════════════════════════════════
   SCENE 3a — FEATURE 1 (frame 270-375 / 9-12.5s, 105 frame)
   "Nota Digital via WA & Print" — bubble WhatsApp + nota, latar hot pink.
   ════════════════════════════════════════════════════════════════════════ */

const ITEMS = [
  { name: "POTONG RAMBUT", qty: "1x", price: "35.000" },
  { name: "CUKUR JENGGOT", qty: "1x", price: "20.000" },
  { name: "CREAMBATH", qty: "1x", price: "60.000" },
];

/** Ekor bubble WhatsApp (kotak diputar). */
const Tail: React.FC<{ side: "left" | "right"; color: string }> = ({ side, color }) => (
  <div
    className="absolute top-5 h-6 w-6 border-4 border-black"
    style={{
      background: color,
      [side === "left" ? "left" : "right"]: -14,
      transform: "rotate(45deg)",
    }}
  />
);

export const Scene3aNota: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const head = popIn(frame, fps, 0, { damping: 10, stiffness: 300, mass: 0.9 });
  const panelIn = spring({ frame: frame - 6, fps, config: { damping: 12, stiffness: 260, mass: 1 } });
  const panelY = interpolate(panelIn, [0, 1], [1100, 0]);
  const panelX = interpolate(panelIn, [0, 1], [420, 0]);

  return (
    <AbsoluteFill style={{ fontFamily: UI, color: INK }}>
      <Bg color={PINK} pattern="dots" patternColor={WHITE} patternOpacity={0.22}>
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-6 px-[40px]">
          {/* ── Headline ── */}
          <div
            className="flex flex-col items-center gap-3"
            style={{ transform: `scale(${head.scale})`, opacity: head.opacity }}
          >
            <div
              className="border-4 border-black bg-white px-7 py-1 text-[72px] uppercase leading-none text-black shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]"
              style={{ fontFamily: DISPLAY }}
            >
              NOTA DIGITAL
            </div>
            <div
              className="border-4 border-black bg-black px-7 py-1 text-[66px] uppercase leading-none text-[#B8FF2F] shadow-[8px_8px_0px_0px_rgba(255,255,255,1)]"
              style={{ fontFamily: DISPLAY }}
            >
              VIA WA &amp; PRINT
            </div>
          </div>

          {/* ── Panel chat ── */}
          <div className="w-[980px]" style={{ transform: `translate(${panelX}px, ${panelY}px)` }}>
            <div className="border-4 border-black bg-white shadow-[12px_12px_0px_0px_rgba(0,0,0,1)]">
              {/* header chat */}
              <div className="flex items-center gap-4 border-b-4 border-black bg-black px-5 py-3">
                <div className="h-14 w-14 rounded-full border-4 border-white bg-[#25D366]" />
                <div className="flex-1">
                  <div className="text-[34px] uppercase leading-none text-white" style={{ fontFamily: DISPLAY }}>
                    KASIR PANGKASIN
                  </div>
                  <div className="text-[24px] text-[#B8FF2F]">online · balas otomatis</div>
                </div>
                <div
                  className="border-4 border-white px-3 py-1 text-[22px] uppercase text-white"
                  style={{ fontFamily: DISPLAY }}
                >
                  WA
                </div>
              </div>

              {/* body chat — pesan menempel ke bawah seperti chat asli */}
              <div className="flex min-h-[860px] flex-col justify-end gap-6 bg-[#efe7dd] px-6 py-7">
                {/* bubble kiri: nota */}
                {(() => {
                  const { scale, opacity } = popIn(frame, fps, 18, { damping: 10, stiffness: 320, mass: 0.85 });
                  return (
                    <div
                      className="relative w-[640px] self-start"
                      style={{ transform: `scale(${scale})`, opacity, transformOrigin: "left bottom" }}
                    >
                      <div className="relative border-4 border-black bg-white px-5 py-4 shadow-[6px_6px_0px_0px_rgba(0,0,0,1)]">
                        <Tail side="left" color={WHITE} />
                        <div className="text-[30px] text-black">Bos, ini nota-nya ya 👌</div>

                        {/* kartu nota */}
                        {(() => {
                          const n = popIn(frame, fps, 30, { damping: 11, stiffness: 300, mass: 0.85 });
                          return (
                            <div
                              className="mt-4 border-4 border-black bg-[#FFFDF5]"
                              style={{ transform: `scale(${n.scale})`, opacity: n.opacity }}
                            >
                              <div className="flex items-center justify-between border-b-4 border-black bg-[#B8FF2F] px-4 py-2">
                                <div className="text-[28px] uppercase" style={{ fontFamily: DISPLAY }}>
                                  NOTA #0231
                                </div>
                                <div className="text-[26px]">PANGKASIN</div>
                              </div>
                              <div className="px-4 py-3">
                                {ITEMS.map((it, i) => {
                                  const r = popIn(frame, fps, 38 + i * 5, SNAPPY);
                                  return (
                                    <div
                                      key={it.name}
                                      className="flex items-center justify-between border-b-2 border-dashed border-black/40 py-2"
                                      style={{
                                        transform: `translateX(${interpolate(r.scale, [0, 1], [40, 0])}px)`,
                                        opacity: r.opacity,
                                      }}
                                    >
                                      <div className="text-[26px] text-black">{it.name}</div>
                                      <div className="text-[26px] text-black">{it.qty}</div>
                                      <div className="text-[26px] text-black" style={{ fontFamily: DISPLAY }}>
                                        {it.price}
                                      </div>
                                    </div>
                                  );
                                })}
                                {(() => {
                                  const t = popIn(frame, fps, 56, { damping: 9, stiffness: 360, mass: 0.8 });
                                  return (
                                    <div
                                      className="mt-4 flex items-center justify-between border-4 border-black bg-black px-4 py-2"
                                      style={{ transform: `scale(${t.scale})`, opacity: t.opacity }}
                                    >
                                      <div className="text-[28px] uppercase text-white" style={{ fontFamily: DISPLAY }}>
                                        TOTAL
                                      </div>
                                      <div className="text-[34px] text-[#B8FF2F]" style={{ fontFamily: DISPLAY }}>
                                        Rp 115.000
                                      </div>
                                    </div>
                                  );
                                })()}
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  );
                })()}

                {/* bubble kanan: konfirmasi */}
                {(() => {
                  const { scale, opacity } = popIn(frame, fps, 66, { damping: 10, stiffness: 320, mass: 0.85 });
                  return (
                    <div
                      className="relative w-[580px] self-end"
                      style={{ transform: `scale(${scale})`, opacity, transformOrigin: "right bottom" }}
                    >
                      <div className="relative border-4 border-black bg-[#B8FF2F] px-5 py-3 shadow-[6px_6px_0px_0px_rgba(0,0,0,1)]">
                        <Tail side="right" color={LIME} />
                        <div className="whitespace-nowrap text-[28px] text-black">
                          Nota masuk &amp; langsung tercatat ✅
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>

          {/* ── Chip print ── */}
          {(() => {
            const { scale, opacity } = popIn(frame, fps, 78, { damping: 10, stiffness: 340, mass: 0.9 });
            return (
              <div
                className="flex items-center gap-4 border-4 border-black bg-white px-7 py-3 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]"
                style={{ transform: `scale(${scale}) rotate(-1.5deg)`, opacity }}
              >
                {/* ikon printer dari div */}
                <div className="relative h-[56px] w-[68px]">
                  <div className="absolute left-3 top-0 h-3 w-10 border-4 border-black bg-white" />
                  <div className="absolute bottom-0 left-0 h-10 w-full border-4 border-black bg-[#22E0FF]" />
                  <div className="absolute bottom-2 left-4 h-2 w-8 bg-black" />
                </div>
                <div className="text-[34px] uppercase" style={{ fontFamily: DISPLAY }}>
                  PRINT STRUK · BLUETOOTH
                </div>
              </div>
            );
          })()}
        </div>
      </Bg>
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
